/* @flow */
import React from 'react';
import Emoji from 'components/Emoji.jsx';
import ShortcodeEmoji from 'components/ShortcodeEmoji.jsx';
import Link from 'components/Link.jsx';
import emojiAnnotationMap from 'emoji-annotation-to-unicode';
import matchStrings from 'utils/matchStrings';
import ModalTrigger from 'components/ModalTrigger.jsx';
import { replace as emojiReplace } from 'lib/emoji';
import Linkify from 'linkify-it';

import GroupMessageListModal from 'groups/components/GroupMessageListModal.jsx';

const linkify = Linkify();

export type EntityType = {
  offset: number,
  length: number,
  renderer: Function,
  data?: Object,
};

export type ParserType = (text: string) => EntityType[];

function renderEmoji(char: string) {
  return <Emoji character={char} />;
}

function renderShortcodeEmoji(code: string) {
  return <ShortcodeEmoji text={code} />;
}

function renderLink(text, rendered, data) {
  return <Link href={data.url} target="_blank">{rendered}</Link>;
}

function getModalForPath(url) {
  const urlParts = url.split('/');
  if (urlParts[3] === 'classes' && urlParts[5] === 'history') {
    return <GroupMessageListModal groupCode={urlParts[4]} />;
  }
  return null;
}

function renderLinkTag(text, rendered, data) {
  const { url } = data;
  const link = <Link href={url} target="_blank">{rendered}</Link>;
  const modal = getModalForPath(url);
  if (modal) {
    return <ModalTrigger modal={modal}>{link}</ModalTrigger>;
  }
  return link;
}

function renderNewline() {
  return <br />;
}

function renderStrong(text, rendered) {
  return <strong>{rendered}</strong>;
}

function renderWhitespace(text) {
  return text.replace(/\t/g, '\u00a0\u00a0').replace(/ /g, '\u00a0');
}

/**
 * @param  {RegExp} regExp   without any capture groups (2nd arg of replace fn must be index)
 * @param  {function} filterFn whether the match qualifies for an entity replacement
 * @param  {function} renderer to render in place of the matching text
 * @return {Entities[]}
 */
function createParser(regExp, filterFn, renderer): ParserType {
  return (text: string) => {
    const entities = [];
    if (text) {
      // $FlowFixMe
      text.replace(regExp, (match, index) => {
        if (!filterFn || filterFn(match)) {
          entities.push({
            offset: index,
            length: match.length,
            renderer,
          });
        }
      });
    }
    return entities;
  };
}

function createUnicodeEmojiParser(): ParserType {
  // like createParser but use the standard emoji replacer,
  // since we don't have access to the regexp it uses.
  return (text: string) => {
    const entities = [];
    emojiReplace(text, (match, index) => {
      entities.push({
        offset: index,
        length: match.length,
        renderer: renderEmoji,
      });
    });
    return entities;
  };
}

function createShortcodeEmojiParser(): ParserType {
  const keys = matchStrings(Object.keys(emojiAnnotationMap));
  const regExp = new RegExp(`:(?:${keys}):`, 'g');
  return createParser(regExp, null, renderShortcodeEmoji);
}

const parseUrls: ParserType = (text: string): EntityType[] => {
  if (text) {
    const matches = linkify.match(text) || [];
    return matches.map(match => ({
      offset: match.index,
      length: match.text.length,
      data: { url: match.url },
      renderer: renderLink,
    }));
  }
  return [];
};

export default {
  parseShortcodeEmoji: createShortcodeEmojiParser(),
  parseNewlines: createParser(/\n/g, null, renderNewline),
  parseLeadingWhitespace: createParser(/^[\t ]+/mg, null, renderWhitespace),
  parseUrls,
  parseAttributes(attributes: []) {
    return attributes.reduce((result, item) => {
      switch (item.type) {
        case 'link':
          item.renderer = renderLinkTag;
          result.push(item);
          break;
        default:
          break;
      }
      return result;
    }, []);
  },
  createTextMatchingParser(text: string): ParserType {
    const replacedText = text.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    return createParser(new RegExp(replacedText, 'ig'), null, renderStrong);
  },
  parseEmoji: createUnicodeEmojiParser(),
};



// WEBPACK FOOTER //
// ./src/lib/TextEntities.js